import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { RadioGroup } from '@/components/ui/radio-group';
import { Select } from '@/components/ui/select';
import { fromEUR, paymentWithin, type FxBasis } from '@/lib/envelopeFx';
import {
  OLD_API_ENVELOPES,
  acceptedBasis,
  amountText,
  emptyHintNotice,
  hintKeyOf,
  rateBasis,
  reduceDraft,
  startDraft,
  submitGate,
  tagProblems,
  tagToRequest,
} from '@/lib/envelopeTag';
import { errorMessage } from '@/lib/errors';
import {
  financeEnvelopeHintQuery,
  financeEnvelopesQuery,
  lineSum,
  newOperationId,
  todayInput,
  useCreateFinanceMovement,
} from '@/lib/finance';
import { fromMinor, toMinor } from '@/lib/money';
import type {
  CreateFinanceMovementRequest,
  FinanceAccount,
  FinanceCategory,
  FinanceCounterparty,
  FinanceEnvelopeHintRequest,
  FinanceLineInput,
  FinanceMovement,
  FinanceMovementLine,
} from '@/api/generated/types.gen';

type LineKind = FinanceLineInput['kind'];
type ExpenseType = NonNullable<FinanceLineInput['expense_type']>;

interface LineDraft {
  key: string;
  kind: LineKind;
  categoryId: string;
  amount: string;
  apartmentId: string;
  counterpartyId: string;
  expenseType: '' | ExpenseType;
  note: string;
  // Carried, not typed. This form has no VAT or booking editor (plan task 23),
  // but a replacement that dropped them rewrote the original's tax and its
  // link to a booking while claiming to be a copy of it (IR05). They travel
  // with the draft and are shown read-only next to the line.
  bookingId: string;
  vatIncluded: boolean;
  vatMinor: number | null;
}

const emptyLine = (kind: LineKind): LineDraft => ({
  key: newOperationId(),
  kind,
  categoryId: '',
  amount: '',
  apartmentId: '',
  counterpartyId: '',
  expenseType: '',
  note: '',
  bookingId: '',
  vatIncluded: false,
  vatMinor: null,
});

// «The hint changed: X» — the offered EUR in the payment's currency at the pair
// of the changed answer (what Apply would then show), or in EUR alone when
// there is no pair.
function changedFigure(suggestedEUR: number, pair: FxBasis | null): string {
  return pair
    ? `${fromMinor(fromEUR(suggestedEUR, pair), pair.currency)} ${pair.currency}`
    : `${fromMinor(suggestedEUR, 'EUR')} EUR`;
}

// Said of the PAYMENT when it converts to nothing (the hint is here and gives no
// pair), whatever the origin of the tag's amount — see the problems below.
const PAYMENT_TOO_SMALL = 'The payment converts to less than 0.01 EUR — too small for an envelope tag.';

// The line kinds an operator can TYPE — the three the "Line type" select
// offers and the only ones `Add line` can produce. `transfer_body` and
// `fx_rounding` are written by the server and have no field here at all.
const TYPEABLE_KINDS: readonly LineKind[] = ['income', 'expense', 'withheld'];

// What a replacement may carry: the three above plus `settle`, which it does
// not type but REPEATS from the payment being reversed (§8.6 R15 — "the same
// targets and lines"). A settle line's target and settled amount are the
// server's: it takes both off the original, pairing the replacement's settle
// lines with the original's BY POSITION. That is why a settle draft is
// immovable below — no kind select, no remove button — while its
// classification stays the operator's to correct.
const REPLACEABLE_KINDS: readonly FinanceMovementLine['kind'][] = [...TYPEABLE_KINDS, 'settle'];

// draftsFrom turns a recorded movement's lines back into form drafts, so a
// replacement can start as a copy of what it replaces (§8.6: the replacement
// is the same payment "теми же целями и строками"). `keepSettle` is on for a
// replacement and off everywhere else, so the ordinary "New payment" form
// still cannot produce a settle line by any route. Lines this form cannot
// reproduce are dropped rather than silently mistyped; if that leaves
// nothing, the caller gets one blank expense line and composes by hand.
//
// IR16: `settle` used to be dropped here with the rest. Replacing a payment
// that settled anything was therefore impossible — the operator got a blank
// expense line, and the server refused the body with «the replacement must
// repeat all settle line(s) of the original».
function draftsFrom(m: FinanceMovement, keepSettle: boolean): LineDraft[] {
  const allowed = keepSettle ? REPLACEABLE_KINDS : TYPEABLE_KINDS;
  const drafts = m.lines
    .filter((l) => allowed.includes(l.kind) && l.category_id)
    .map((l) => ({
      key: newOperationId(),
      kind: l.kind as LineKind,
      categoryId: l.category_id ?? '',
      amount: fromMinor(l.amount_minor, m.currency),
      apartmentId: l.apartment_id ?? '',
      counterpartyId: l.counterparty_id ?? '',
      expenseType: (l.expense_type ?? '') as '' | ExpenseType,
      note: l.note ?? '',
      bookingId: l.booking_id ?? '',
      vatIncluded: l.vat_included,
      vatMinor: l.vat_minor ?? null,
    }));
  return drafts.length > 0 ? drafts : [emptyLine('expense')];
}

export interface MovementFormProps {
  accounts: readonly FinanceAccount[];
  categories: readonly FinanceCategory[];
  counterparties?: readonly FinanceCounterparty[];
  apartments?: readonly { id: string; title: string }[];
  /** From finance settings — decides what "today" means for the date field. */
  businessTz?: string;
  onDone?: (movement: FinanceMovement) => void;
  onCancel?: () => void;
  /**
   * When given, the composed body is handed over instead of being posted.
   * The reversal dialog needs exactly this body as its `replacement`, and
   * duplicating the Σ rule in a second form is how the two would drift.
   */
  onCompose?: (body: CreateFinanceMovementRequest) => void;
  submitLabel?: string;
  /**
   * The CALLER's request is in flight. In onCompose mode this form owns no
   * mutation, so without this the button never looks busy and a second click
   * composes a second request.
   */
  busy?: boolean;
  /** Extra reasons the caller will not accept a submit yet. */
  extraProblems?: readonly string[];
  /**
   * Start the form as a copy of this movement — account, currency,
   * direction, date, amount and typeable lines. The reversal dialog passes
   * the payment being reversed: its replacement is "the payment that should
   * have been there", so every field the operator did NOT come to change has
   * to arrive already right. Without it the account fell back to accounts[0]
   * and a corrected article silently landed on a different account.
   */
  initialFrom?: FinanceMovement;
  /**
   * This form is composing the REPLACEMENT of a reversed payment (§8.6 R15),
   * not an ordinary new one. It is what lets `initialFrom`'s settle line(s)
   * survive into the drafts; everywhere else a settle line has no target to
   * inherit and the server refuses it (422). Declared rather than inferred
   * from `initialFrom` so a future "duplicate this payment" caller cannot
   * pick the behaviour up by accident.
   */
  replacement?: boolean;
  /**
   * The operator holds finance.manage. Without it an INCOMING payment shows
   * no envelope choice and asks for no hint (envelopes spec §6): a tag «into
   * the envelope» needs manage, and the server checks it anyway.
   */
  canManage?: boolean;
  /**
   * The caller's refusal that belongs to the envelope field — the reversal's
   * 409 envelope_insufficient: the server checked the whole plan (mirrors and
   * this tag) and it does not fit; the detail names the envelope. Shown at the
   * field, or in the general alert while the field is hidden.
   */
  envelopeFailure?: string | null;
  /** The operator changed the envelope tag — a refusal of the old one is stale. */
  onEnvelopeEdit?: () => void;
}

/**
 * Record one direct payment: a bank amount on an account, split into lines.
 *
 * Two server rules drive the whole form, and both are worth stating because
 * getting either wrong turns a click into a 422 the operator cannot read:
 *
 *  - the header equation (internal/domain/finance/equation.go) is
 *    income + expense − withheld, NOT the sum of every line. The submit is
 *    gated on that identity and the gap is shown while it does not hold.
 *  - a line's article has to agree with the line's kind: an income line may
 *    not carry an expense category or vice versa (movements.go), inactive
 *    articles are refused, and an article with requires_counterparty needs
 *    one. The selects only offer what the server will take.
 *
 * The operation_id is minted once per mounted form and reused on every
 * attempt (§5.19): a fresh key per retry would turn one timeout into two
 * payments.
 */
export function MovementForm({
  accounts,
  categories,
  counterparties,
  apartments,
  businessTz,
  onDone,
  onCancel,
  onCompose,
  submitLabel = 'Record payment',
  busy,
  extraProblems,
  initialFrom,
  replacement = false,
  canManage = false,
  envelopeFailure,
  onEnvelopeEdit,
}: MovementFormProps) {
  const first = accounts[0];
  const [accountId, setAccountId] = useState(initialFrom?.account_id ?? first?.id ?? '');
  const [currency, setCurrency] = useState(initialFrom?.currency ?? first?.currencies[0] ?? '');
  const [direction, setDirection] = useState<'in' | 'out'>(initialFrom?.direction ?? 'out');
  // The original's own date: the payment that should have been there belongs
  // in the month it belonged to, not in the month the mistake was noticed.
  // If that month is closed the server refuses it and says so — this form
  // deliberately knows nothing about closed months (see ReverseDialog).
  const [occurredOn, setOccurredOn] = useState(
    () => initialFrom?.occurred_on ?? todayInput(businessTz),
  );
  const [amount, setAmount] = useState(
    initialFrom ? fromMinor(initialFrom.amount_minor, initialFrom.currency) : '',
  );
  const [lines, setLines] = useState<LineDraft[]>(() =>
    initialFrom ? draftsFrom(initialFrom, replacement) : [emptyLine('expense')],
  );
  const [note, setNote] = useState(initialFrom?.note ?? '');
  const [operationId] = useState(newOperationId);
  const [failure, setFailure] = useState<string | null>(null);

  const account = accounts.find((a) => a.id === accountId);
  const create = useCreateFinanceMovement();

  const activeCategories = useMemo(() => categories.filter((c) => c.is_active), [categories]);

  const headerMinor = currency ? toMinor(amount, currency) : null;
  // Memoised so the envelope hint's input (below) keeps its identity across
  // renders that change nothing it reads.
  const lineMinors = useMemo(
    () => lines.map((l) => (currency ? toMinor(l.amount, currency) : null)),
    [lines, currency],
  );
  const sum = lineSum(
    lines.flatMap((l, i) => {
      const m = lineMinors[i];
      return m === null || m === undefined ? [] : [{ kind: l.kind, minor: m }];
    }),
  );
  const allLinesReadable = lineMinors.every((m) => m !== null && m > 0);
  const diff = headerMinor === null ? null : sum - headerMinor;

  // --- The envelope side (envelopes spec §8 «Форма», §8.1) ----------------
  //
  // The tag lives in one reducer with the server's hint, bound to the input
  // the hint was computed for; an automatic suggestion is frozen once accepted
  // (envelopeTag.ts). A replacement copies the original's single tag, keeps an untagged original
  // untagged, or asks for an explicit choice; an older API disables it all.
  const ownMoves = useMemo(
    () =>
      replacement && initialFrom
        ? (initialFrom.envelope_moves ?? []).filter((m) => !m.reverses_id)
        : [],
    [replacement, initialFrom],
  );
  // The original is KNOWN to carry no tag of its own. Keyed on the array: an
  // older API sends no envelope_moves at all, which says nothing about a tag
  // (the reversal dialog keeps that replacement unavailable).
  const knownEmpty = replacement && Array.isArray(initialFrom?.envelope_moves) && ownMoves.length === 0;
  // The tag a replacement copies: the original's single own move in the
  // original's direction — and only while the form keeps that direction.
  // Flipped, the copy would mean the opposite («from envelope A» read as
  // «into envelope A»), so the operator chooses instead (the radio below).
  const copyableFor = (dir: 'in' | 'out') => {
    const original = initialFrom?.direction;
    const only = ownMoves.length === 1 ? ownMoves[0] : undefined;
    return only && only.direction === original && dir === original ? only : undefined;
  };
  const copyable = copyableFor(direction);
  const needsChoice = ownMoves.length > 0 && !copyable;
  // A tag can exist in this direction at all: an income takes one only with
  // finance.manage (envelopes spec §6).
  const canTag = direction === 'out' || canManage;
  // The choice is asked only where a tag can exist. Where none can, the
  // replacement carries none by necessity; a flip back asks again, since
  // every Direction change resets the choice.
  const asksChoice = needsChoice && canTag;
  // Where the envelope side starts in a direction: on mount, and over again
  // on every Direction change (the select's handler below).
  const initialDraft = (dir: 'in' | 'out') => startDraft(copyableFor(dir), knownEmpty);
  const [legacyChoice, setLegacyChoice] = useState<'tag' | 'none' | null>(null);
  const [draft, dispatch] = useReducer(reduceDraft, direction, initialDraft);
  const tag = draft.tag;
  // The form's own request or the caller's is in flight. The Direction and
  // the choice between one new tag and none hold still meanwhile: either
  // could hide the envelope block a refusal of the tag is on its way to.
  const pending = create.isPending || !!busy;

  // available_on gives every active envelope what one spend dated that day
  // can take (envelopes spec §4). The previous date's list stays on screen
  // while the next one loads, but its availability is not used
  // (isPlaceholderData).
  const envelopesQuery = useQuery({
    ...financeEnvelopesQuery({ availableOn: occurredOn || undefined }),
    placeholderData: keepPreviousData,
  });
  const envelopesData = envelopesQuery.data;
  const envelopesLoaded = envelopesData !== undefined;
  // An older API answers without `envelopes`. That is «unavailable», never
  // «no envelopes».
  const oldApi = envelopesLoaded && !Array.isArray(envelopesData.envelopes);
  const activeEnvelopes = useMemo(
    () => (Array.isArray(envelopesData?.envelopes) ? envelopesData.envelopes : []).filter((e) => e.is_active),
    [envelopesData],
  );
  const activeIds = useMemo(() => new Set(activeEnvelopes.map((e) => e.id)), [activeEnvelopes]);
  const nameOf = (id: string) =>
    activeEnvelopes.find((e) => e.id === id)?.name ??
    ownMoves.find((m) => m.envelope_id === id)?.envelope_name ??
    'The envelope';
  const wantsEnvelope = !oldApi && canTag && (!needsChoice || legacyChoice === 'tag');
  const showEnvelope = wantsEnvelope && envelopesLoaded;
  // A failed list is no dead end. No tag can be offered or checked without
  // it, but the operator may still say «No envelope tag».
  const envelopesFailed = wantsEnvelope && !envelopesLoaded && envelopesQuery.isError;

  // The CURRENT input (not the debounced one) and its key.
  const hintBody = useMemo<FinanceEnvelopeHintRequest | null>(() => {
    if (!showEnvelope || !currency || !occurredOn || headerMinor === null || headerMinor <= 0) return null;
    const ls = lines.flatMap((l, i) => {
      const m = lineMinors[i];
      return l.categoryId && m !== null && m !== undefined && m > 0
        ? [{ category_id: l.categoryId, amount_minor: m }]
        : [];
    });
    if (ls.length === 0) return null;
    return { direction, currency, amount_minor: headerMinor, occurred_on: occurredOn, lines: ls };
  }, [showEnvelope, currency, occurredOn, headerMinor, direction, lines, lineMinors]);
  const currentKey = hintKeyOf(hintBody);
  const currentKeyRef = useRef(currentKey);
  currentKeyRef.current = currentKey;
  const hintBodyRef = useRef(hintBody);
  hintBodyRef.current = hintBody;
  useEffect(() => dispatch({ type: 'input', key: currentKey }), [currentKey]);

  // Debounce: after 300 ms of quiet the request goes out for THAT key. Keyed
  // on the key alone — a line note changes `lines` but not what is asked.
  const [requested, setRequested] = useState<{ key: string; body: FinanceEnvelopeHintRequest } | null>(null);
  useEffect(() => {
    if (currentKey === null) return;
    const key = currentKey;
    const t = setTimeout(() => {
      const body = hintBodyRef.current;
      if (!body || hintKeyOf(body) !== key) return;
      dispatch({ type: 'request', key, current: currentKeyRef.current });
      setRequested({ key, body });
    }, 300);
    return () => clearTimeout(t);
  }, [currentKey]);

  const hintQuery = useQuery(financeEnvelopeHintQuery(requested?.body ?? null));
  // Every settled answer is handed to the reducer WITH its own key; the
  // reducer drops an answer whose key is not the current one, and never
  // re-applies an answer to the tag for the key it already accepted (an
  // automatic tag is offered «Apply» instead; a manual amount just converts at
  // the newest answer). dataUpdatedAt is what lets a re-answer for the same key
  // (invalidation) reach it at all.
  useEffect(() => {
    if (!requested || !hintQuery.data || hintQuery.isFetching) return;
    dispatch({ type: 'result', key: requested.key, hint: hintQuery.data, current: currentKeyRef.current });
  }, [hintQuery.data, hintQuery.dataUpdatedAt, hintQuery.isFetching, requested]);
  useEffect(() => {
    if (!requested || !hintQuery.isError || hintQuery.isFetching) return;
    dispatch({
      type: 'failure',
      key: requested.key,
      current: currentKeyRef.current,
      message: errorMessage(hintQuery.error),
    });
  }, [hintQuery.isError, hintQuery.error, hintQuery.errorUpdatedAt, hintQuery.isFetching, requested]);
  const retryHint = () => {
    if (!requested) return;
    dispatch({ type: 'request', key: requested.key, current: currentKeyRef.current });
    void hintQuery.refetch();
  };

  const hint = draft.status.kind === 'ready' && draft.status.forKey === currentKey ? draft.status.hint : null;
  // The conversion pair of THIS input (OH-209): a EUR payment converts 1:1,
  // any other currency pairs its header with the hint's EUR header. For a
  // manual tag (and an explicit «None») `hint` is the NEWEST answer for the
  // input, in either order of answer and tag: an answer that comes after the tag
  // became the operator's replaces the accepted one in reduceDraft, and one that
  // came before it (kept aside while the tag was automatic or open) is adopted by
  // the pick or the typing that makes the tag manual — so a rate corrected while
  // the form is open reaches the amount before it is sent once the server answers
  // the input again (an invalidation after a finance write in this tab, or a
  // return to the input). An auto tag stays on the answer it was accepted from:
  // its EUR is sent as it is; the pair only shows it and checks it against the
  // header. Validation and the request use exactly this.
  const basis = rateBasis(currency, headerMinor, hint);
  // What the field and its hint SHOW may lag one keystroke behind: while the
  // answer for the new input is on its way, an amount that came in EUR keeps
  // showing against the pair it was computed for instead of blanking. It is
  // never what is validated or sent.
  const accepted = acceptedBasis(draft);
  const shownBasis = basis ?? (accepted?.currency === currency ? accepted : null);
  // Σ of the lines whose article defaults to the envelope — what a pick starts
  // on (spec §2.1). Any line kind: the server's hint counts them the same way.
  const lineSumFor = (envelopeId: string) =>
    lines.reduce((acc, l, i) => {
      const m = lineMinors[i];
      const defaultsHere = categories.find((c) => c.id === l.categoryId)?.default_envelope_id === envelopeId;
      return defaultsHere && m !== null && m !== undefined && m > 0 ? acc + m : acc;
    }, 0);
  const chosen = tag.kind === 'none' ? undefined : activeEnvelopes.find((e) => e.id === tag.envelopeId);
  // Available on the payment's date — the hint's figure for an automatic
  // tag, the list's for the operator's own choice. Spends only.
  const availableMinor =
    direction !== 'out' || tag.kind === 'none'
      ? null
      : tag.kind === 'auto'
        ? (hint?.available_eur_minor ?? null)
        : envelopesQuery.isPlaceholderData
          ? null
          : (chosen?.available_eur_minor ?? null);
  const gate = showEnvelope ? submitGate(draft, currentKey, currency) : ({ ok: true } as const);
  const amountCtx = { currency, headerMinor, basis, availableMinor, replacement, lineSumFor };
  const shownCtx = { ...amountCtx, basis: shownBasis };
  const emptyNotice = tag.kind === 'none' && !draft.dismissed ? emptyHintNotice(hint, nameOf) : null;
  // A tag on an envelope the list does not offer (a copy of a switched-off
  // one) is shown as what it is rather than as «None» — tagProblems names it.
  const envelopeOptions = [
    { value: '', label: 'None' },
    ...activeEnvelopes.map((e) => ({ value: e.id, label: e.name })),
    ...(tag.kind !== 'none' && !activeIds.has(tag.envelopeId)
      ? [{ value: tag.envelopeId, label: `${nameOf(tag.envelopeId)} (switched off)` }]
      : []),
  ];
  // An older API on a replacement whose original carried a tag: dropping the
  // tag would change the payment, so the replacement itself is unavailable.
  const oldApiBlocksReplacement = oldApi && ownMoves.length > 0;

  // Every reason the form will not submit, in the operator's words. The list
  // being non-empty IS the disabled state, so the button and the explanation
  // can never disagree.
  const problems: string[] = [...(extraProblems ?? [])];
  if (!accountId || !currency) problems.push('Choose an account and a currency.');
  if (!occurredOn) problems.push('Choose the date the money moved.');
  if (headerMinor === null || headerMinor <= 0) {
    problems.push(`Enter the amount that hit the account, in ${currency || 'the account currency'}.`);
  }
  if (!allLinesReadable) problems.push('Every line needs an amount above zero.');
  lines.forEach((l, i) => {
    const cat = activeCategories.find((c) => c.id === l.categoryId);
    if (!cat) {
      problems.push(`Line ${i + 1} needs an article.`);
      return;
    }
    if (cat.requires_counterparty && !l.counterpartyId) {
      problems.push(`Line ${i + 1}: ${cat.name} needs a counterparty.`);
    }
  });
  if (headerMinor !== null && headerMinor > 0 && allLinesReadable && diff !== 0 && diff !== null) {
    problems.push(`Difference ${fromMinor(Math.abs(diff), currency)} ${currency}`);
  }
  if (oldApiBlocksReplacement) {
    problems.push(OLD_API_ENVELOPES);
  } else if (!oldApi) {
    if (asksChoice && legacyChoice === null) problems.push('Choose the envelope side of this replacement.');
    if (wantsEnvelope && !envelopesLoaded) {
      if (!envelopesQuery.isError) problems.push('Loading the envelopes…');
      else if (!draft.dismissed) problems.push("Couldn't load the envelopes.");
    }
    if (showEnvelope) {
      problems.push(...tagProblems(tag, { ...amountCtx, activeIds, nameOf, occurredOn }));
      // The answer for this very input is here and still gives no pair. A EUR
      // payment's pair never depends on the hint, so this is a payment in
      // another currency whose header converts to 0.00 EUR. Nothing can be
      // tagged on it, whatever the origin of the amount: a typed or default
      // one has no EUR to send, and one that came in EUR (a copy, an automatic
      // tag picked again) cannot fit under a header of nothing. The fault is
      // the PAYMENT's, so it is said of the payment. A non-empty list IS the
      // disabled state: the button must not stay enabled and do nothing.
      // (Without a pair tagProblems never says LESS_THAN_A_CENT, so no repeat.)
      if (tag.kind !== 'none' && hint !== null && basis === null) problems.push(PAYMENT_TOO_SMALL);
      // The gate's state in short; the block below carries the server's
      // reason and the way out.
      if (!gate.ok) {
        problems.push(gate.reason === 'error' ? "Couldn't get the envelope hint" : 'Calculating the envelope hint…');
      }
    }
  }

  function submit() {
    if (problems.length > 0 || busy || headerMinor === null || !gate.ok) return;
    // Always an explicit amount; a hidden block sends nothing. Defence in
    // depth: a chosen tag that cannot be converted is already a problem above,
    // and is not dropped silently here either — nothing is sent.
    const envelope = showEnvelope ? tagToRequest(tag, amountCtx) : undefined;
    if (showEnvelope && tag.kind !== 'none' && !envelope) return;
    setFailure(null);
    const body: CreateFinanceMovementRequest = {
      operation_id: operationId,
      account_id: accountId,
      currency,
      amount_minor: headerMinor,
      direction,
      occurred_on: occurredOn,
      lines: lines.map((l, i) => {
        const line: FinanceLineInput = {
          kind: l.kind,
          amount_minor: lineMinors[i] as number,
          category_id: l.categoryId,
        };
        if (l.expenseType) line.expense_type = l.expenseType;
        if (l.apartmentId) line.apartment_id = l.apartmentId;
        if (l.bookingId) line.booking_id = l.bookingId;
        if (l.counterpartyId) line.counterparty_id = l.counterpartyId;
        // vat_included defaults to false on the schema and vat_minor is
        // optional, so only what the original actually carried is sent.
        if (l.vatIncluded) line.vat_included = true;
        if (l.vatMinor !== null) line.vat_minor = l.vatMinor;
        if (l.note.trim()) line.note = l.note.trim();
        return line;
      }),
    };
    if (envelope) body.envelope = envelope;
    if (note.trim()) body.note = note.trim();

    if (onCompose) {
      onCompose(body);
      return;
    }
    create.mutate(body, {
      onSuccess: (res) => onDone?.(res.movement),
      onError: (err) => setFailure(errorMessage(err)),
    });
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Account"
          value={accountId}
          options={accounts.map((a) => ({ value: a.id, label: a.name }))}
          onChange={(e) => {
            const next = accounts.find((a) => a.id === e.target.value);
            setAccountId(e.target.value);
            // The currency list belongs to the account: keeping the old one
            // would post a pair the account does not hold (422).
            setCurrency(next?.currencies[0] ?? '');
          }}
        />
        <Select
          label="Currency"
          value={currency}
          options={(account?.currencies ?? []).map((c) => ({ value: c, label: c }))}
          onChange={(e) => setCurrency(e.target.value)}
        />
        <Select
          label="Direction"
          value={direction}
          options={[
            { value: 'out', label: 'Out — money left the account' },
            { value: 'in', label: 'In — money arrived' },
          ]}
          disabled={pending}
          onChange={(e) => {
            const next = e.target.value === 'in' ? 'in' : 'out';
            if (next === direction) return;
            setDirection(next);
            // Every flip starts the envelope side over as it would have
            // started in that direction (startDraft): nothing chosen in one
            // direction — a pick, a «None», the choice below — carries into
            // the other, where it would mean the opposite. That includes a
            // flip that hides the block (an income without finance.manage).
            dispatch({ type: 'reset', to: initialDraft(next) });
            setLegacyChoice(null);
            // The tag a refusal was about is gone with it.
            onEnvelopeEdit?.();
          }}
        />
        <Input
          label="Date"
          type="date"
          value={occurredOn}
          onChange={(e) => setOccurredOn(e.target.value)}
        />
        <MoneyInput
          label="Amount"
          currency={currency || 'EUR'}
          value={amount}
          onChange={setAmount}
          hint="What the account moved, before the split below"
        />
      </div>

      <div className="flex flex-col gap-4">
        {lines.map((l, i) => (
          <LineFields
            key={l.key}
            index={i}
            line={l}
            currency={currency || 'EUR'}
            categories={activeCategories}
            counterparties={counterparties}
            apartments={apartments}
            onChange={(patch) =>
              setLines((prev) => prev.map((p, j) => (i === j ? { ...p, ...patch } : p)))
            }
            // A settle line is never removable: the server pairs the
            // replacement's settle lines with the original's BY POSITION, so
            // dropping one moves every later target onto the wrong
            // obligation, and its «repeat all settle line(s)» check only
            // notices the wrong COUNT. Ordinary lines stay removable — the
            // server matches none of them to a target.
            onRemove={
              l.kind !== 'settle' && lines.length > 1
                ? () => setLines((prev) => prev.filter((_, j) => j !== i))
                : undefined
            }
          />
        ))}
        <div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLines((prev) => [...prev, emptyLine(direction === 'in' ? 'income' : 'expense')])}
          >
            <Plus className="h-4 w-4" />
            Add line
          </Button>
        </div>
      </div>

      <div className="rounded-[var(--radius-card)] bg-[var(--surface-sunken)] px-4 py-3 text-sm">
        <p className="tabular-nums">
          Lines sum to{' '}
          <strong>{currency ? `${fromMinor(sum, currency)} ${currency}` : '—'}</strong>
          {' · header '}
          <strong>{headerMinor === null ? '—' : `${fromMinor(headerMinor, currency)} ${currency}`}</strong>
        </p>
        {problems.length > 0 && (
          <ul className="mt-1 flex flex-col gap-0.5 text-[var(--ollio-gold-deep)]">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </div>

      {oldApi && !oldApiBlocksReplacement && (
        <p className="text-sm text-[var(--ollio-gold-deep)]">{OLD_API_ENVELOPES}</p>
      )}
      {asksChoice && !oldApi && (
        // A payment from before OH-195 may carry several moves, or one
        // against its own direction (envelopes spec §8.1); a copied tag whose
        // direction the operator flipped lands here too. The moves are shown
        // as they were; the replacement's envelope side is the operator's
        // explicit call — wherever a tag can exist at all (asksChoice).
        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted">
            Was:{' '}
            {ownMoves
              .map((m) => `${m.direction === 'out' ? 'from' : 'into'} ${m.envelope_name} ${fromMinor(m.amount_eur_minor, 'EUR')} EUR`)
              .join(', ')}
          </p>
          <RadioGroup
            value={legacyChoice ?? ''}
            disabled={pending}
            onChange={(v) => {
              setLegacyChoice(v === 'tag' ? 'tag' : 'none');
              // The envelope side changed, as with a flip: a refusal of the
              // tag that was there is stale.
              onEnvelopeEdit?.();
            }}
            options={[
              { value: 'tag', label: 'One new envelope tag' },
              { value: 'none', label: 'No envelope tag' },
            ]}
          />
        </div>
      )}
      {envelopesFailed && (
        <div data-testid="envelope-block" className="flex flex-wrap items-center gap-2 text-sm">
          <span>Couldn't load the envelopes: {errorMessage(envelopesQuery.error)}</span>
          <Button variant="secondary" size="sm" onClick={() => void envelopesQuery.refetch()}>
            Reload envelopes
          </Button>
          {draft.dismissed ? (
            <span className="text-muted">No envelope tag — the payment is recorded without one.</span>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                dispatch({ type: 'pick', envelopeId: '' });
                onEnvelopeEdit?.();
              }}
            >
              No envelope tag
            </Button>
          )}
        </div>
      )}
      {showEnvelope && (
        <div data-testid="envelope-block" className="grid gap-4 sm:grid-cols-2">
          <Select
            label={direction === 'out' ? 'From envelope' : 'Into envelope'}
            value={tag.kind === 'none' ? '' : tag.envelopeId}
            options={envelopeOptions}
            onChange={(e) => {
              dispatch({ type: 'pick', envelopeId: e.target.value });
              onEnvelopeEdit?.();
            }}
          />
          {tag.kind !== 'none' && (
            <MoneyInput
              label={currency ? `Envelope amount (${currency})` : 'Envelope amount'}
              currency={currency || 'EUR'}
              value={amountText(tag, shownCtx)}
              onChange={(v) => {
                // Typing over a suggestion or a default makes it the operator's (manual).
                dispatch({ type: 'amount', text: v });
                onEnvelopeEdit?.();
              }}
              hint={
                availableMinor === null
                  ? undefined // the currency code, MoneyInput's own
                  : shownBasis !== null && currency !== 'EUR'
                    ? `Available on ${occurredOn}: ${fromMinor(paymentWithin(availableMinor, shownBasis), currency)} ${currency} (${fromMinor(availableMinor, 'EUR')} EUR)`
                    : `Available on ${occurredOn}: ${fromMinor(availableMinor, 'EUR')} EUR`
              }
            />
          )}
          {emptyNotice && <p className="text-sm text-muted sm:col-span-2">{emptyNotice}</p>}
          {!gate.ok && gate.reason === 'error' && (
            <div className="flex flex-wrap items-center gap-2 text-sm sm:col-span-2">
              <span>Couldn't get the envelope hint: {gate.message}</span>
              <Button variant="secondary" size="sm" onClick={retryHint}>
                Retry
              </Button>
              <span className="text-muted">
                {/* Another envelope would wait for the same answer: in another currency only None gets out. */}
                {currency === 'EUR' ? '— or pick an envelope or None by hand.' : '— or pick None by hand.'}
              </span>
            </div>
          )}
          {draft.changed && (
            <div className="flex flex-wrap items-center gap-2 text-sm sm:col-span-2">
              <span>
                {draft.changed.envelope_id && draft.changed.suggested_eur_minor > 0
                  ? `The hint changed: ${changedFigure(draft.changed.suggested_eur_minor, rateBasis(currency, headerMinor, draft.changed))} available now`
                  : 'The hint changed: no envelope is suggested now'}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  dispatch({ type: 'apply' });
                  onEnvelopeEdit?.();
                }}
              >
                Apply
              </Button>
            </div>
          )}
          {envelopeFailure && (
            <p role="alert" className="text-sm text-destructive sm:col-span-2">
              {envelopeFailure}
            </p>
          )}
        </div>
      )}

      <Input label="Note" multiline value={note} onChange={(e) => setNote(e.target.value)} />

      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      )}
      {/* The envelope block holds the refusal while it is shown. Every change
          the operator makes to the tag (a pick, «None», an amount, «No
          envelope tag», a Direction change) clears the refusal through
          onEnvelopeEdit; what hides the block without touching the tag — a
          list that failed meanwhile, e.g. for a new date — leaves it
          standing, so it is shown here: never nowhere. */}
      {envelopeFailure && !showEnvelope && (
        <p role="alert" className="text-sm text-destructive">
          {envelopeFailure}
        </p>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        )}
        <Button
          variant="primary"
          onClick={submit}
          disabled={problems.length > 0 || !!busy}
          loading={create.isPending || !!busy}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function LineFields({
  index,
  line,
  currency,
  categories,
  counterparties,
  apartments,
  onChange,
  onRemove,
}: {
  index: number;
  line: LineDraft;
  currency: string;
  categories: readonly FinanceCategory[];
  counterparties?: readonly FinanceCounterparty[];
  apartments?: readonly { id: string; title: string }[];
  onChange: (patch: Partial<LineDraft>) => void;
  onRemove?: () => void;
}) {
  // movements.go refuses an income line on an expense article and an expense
  // line on an income article. A withheld line is neither, and neither is a
  // settle line (its kind check is about the target, not the article), so
  // both may carry any active article.
  const allowed = categories.filter((c) => {
    if (line.kind === 'income') return c.kind !== 'expense';
    if (line.kind === 'expense') return c.kind !== 'income';
    return true;
  });
  // A repeated settle line (§8.6 R15). Its kind cannot change and the line
  // cannot be removed (see onRemove at the call site) — the server pairs
  // these with the original's settle lines by position. Everything else on
  // it is editable: whatever the operator leaves as it arrived, the server
  // re-inherits from the original line; whatever they change, wins.
  const isSettle = line.kind === 'settle';

  // What this line carries but cannot show as a field.
  const kept: string[] = [];
  if (line.vatMinor !== null) kept.push(`VAT ${fromMinor(line.vatMinor, currency)}`);
  else if (line.vatIncluded) kept.push('VAT');
  if (line.bookingId) kept.push('the booking link');

  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Line {index + 1}
        </span>
        {onRemove && (
          <Button variant="ghost" size="sm" onClick={onRemove} aria-label={`Remove line ${index + 1}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {isSettle ? (
          // Deliberately NOT a disabled <Select>: there is no other value it
          // could ever take, and a disabled control invites the operator to
          // look for the one that unlocks it. This is a statement of what
          // the line is.
          <p className="text-sm text-muted sm:col-span-2">
            <strong className="text-[var(--text-heading)]">Settlement.</strong> This line settles the
            obligation the payment being replaced settled — the target and the amount credited to it
            come from that payment and cannot be changed here. The article and the rest below are
            yours to correct.
          </p>
        ) : (
          <Select
            label="Line type"
            value={line.kind}
            options={[
              { value: 'expense', label: 'Expense' },
              { value: 'income', label: 'Income' },
              { value: 'withheld', label: 'Withheld by the channel' },
            ]}
            onChange={(e) => {
              const kind = e.target.value as LineKind;
              // The chosen article may be illegal for the new kind — clearing
              // it is the honest outcome; keeping it would post a 422.
              const keep = categories.find((c) => c.id === line.categoryId);
              const stillOk =
                keep &&
                (kind === 'withheld' ||
                  (kind === 'income' ? keep.kind !== 'expense' : keep.kind !== 'income'));
              onChange({ kind, categoryId: stillOk ? line.categoryId : '' });
            }}
          />
        )}
        <Select
          label="Article"
          value={line.categoryId}
          placeholder="Pick an article"
          options={allowed.map((c) => ({ value: c.id, label: c.name }))}
          onChange={(e) => {
            const cat = categories.find((c) => c.id === e.target.value);
            onChange({
              categoryId: e.target.value,
              // The article's default expense type is what the server would
              // apply anyway; showing it beats leaving the field blank.
              expenseType: cat?.expense_type_default ?? '',
            });
          }}
        />
        <MoneyInput
          label="Line amount"
          currency={currency}
          value={line.amount}
          onChange={(v) => onChange({ amount: v })}
        />
        <Select
          label="Expense type"
          value={line.expenseType}
          // Enabled empty option rather than Select's `placeholder`, which is
          // disabled (ui/select.tsx): these three are optional and have to be
          // clearable. The Article select keeps its placeholder — an empty
          // article is not a legal choice.
          options={[
            { value: '', label: 'None' },
            { value: 'operational', label: 'Operational' },
            { value: 'capital', label: 'Capital' },
            { value: 'financing', label: 'Financing' },
          ]}
          onChange={(e) => onChange({ expenseType: e.target.value as '' | ExpenseType })}
        />
        <Select
          label="Counterparty"
          value={line.counterpartyId}
          options={[
            { value: '', label: 'None' },
            ...(counterparties ?? [])
              .filter((c) => c.is_active)
              .map((c) => ({ value: c.id, label: c.name })),
          ]}
          onChange={(e) => onChange({ counterpartyId: e.target.value })}
        />
        <Select
          label="Apartment"
          value={line.apartmentId}
          options={[
            { value: '', label: 'None' },
            ...(apartments ?? []).map((a) => ({ value: a.id, label: a.title })),
          ]}
          onChange={(e) => onChange({ apartmentId: e.target.value })}
        />
        <Input
          label="Line note"
          value={line.note}
          onChange={(e) => onChange({ note: e.target.value })}
          className="sm:col-span-2"
        />
        {kept.length > 0 && (
          // Shown rather than carried in silence: these two ride along from
          // the payment being replaced and there is no field here to change
          // them, so the operator has to be able to SEE what the replacement
          // will file. A carried VAT figure does NOT follow an edited line
          // amount — this form has no VAT editor — which is precisely why it
          // is on screen instead of travelling invisibly.
          <p className="sm:col-span-2 text-xs text-muted">
            Kept from the original: {kept.join(', ')}
          </p>
        )}
      </div>
    </div>
  );
}
